import {
    PipelineDefinition,
    PipelineStepInstance,
} from './pipelineBuilder.types'
import {
    defaultConversionRules,
    enabledTriggersFromRules,
    normalizeConversionRules,
    rulesHavePmApproval,
} from './conversionRulesUtils'
import {
    defaultLeadsExitTransitionConfig,
    normalizeLeadsExitConfig,
} from './exitFromLeadsUtils'
import { getEnabledStepsInOrder } from './stepOrder'
import { TransitionConfig } from './transitionConfig.types'
import {
    defaultExitModeForFromType,
    defaultTriggersForStep,
    getTransitionTriggerOptions,
    PM_MANUAL_TRIGGER,
} from './transitionTriggerCatalog'
import { transitionId } from './transitionUtils'

function normalizeTriggers(
    fromType: PipelineStepInstance['type'],
    rawTriggers: string[] | undefined,
): string[] {
    const allowed = new Set(
        getTransitionTriggerOptions(fromType).map((option) => option.value),
    )
    if (!rawTriggers?.length) {
        return defaultTriggersForStep(fromType)
    }
    const filtered = rawTriggers.filter((trigger) => allowed.has(trigger))
    return filtered.length > 0 ? filtered : defaultTriggersForStep(fromType)
}

function readLegacyTriggers(
    stored: TransitionConfig & { lead_conversion_triggers?: string[] },
): string[] | undefined {
    if (stored.triggers?.length) {
        return stored.triggers
    }
    if (stored.lead_conversion_triggers?.length) {
        return stored.lead_conversion_triggers
    }
    return undefined
}

export function isLeadsExitFromStep(fromStep: PipelineStepInstance): boolean {
    return fromStep.type === 'leads'
}

export function defaultTransitionConfig(
    fromStep: PipelineStepInstance,
): TransitionConfig {
    if (isLeadsExitFromStep(fromStep)) {
        return defaultLeadsExitTransitionConfig()
    }

    return {
        exit_mode: defaultExitModeForFromType(fromStep.type),
        triggers: defaultTriggersForStep(fromStep.type),
        require_pm_approval: false,
    }
}

export function isProspectsToLeadsTransition(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
): boolean {
    return fromStep.type === 'prospects' && toStep.type === 'leads'
}

export function getTransitionConfig(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
    transitionConfigs?: PipelineDefinition['transition_configs'],
): TransitionConfig {
    const key = transitionId(fromStep.id, toStep.id)
    const stored = transitionConfigs?.[key] as
        | (TransitionConfig & { lead_conversion_triggers?: string[] })
        | undefined
    const isConversionEdge = isProspectsToLeadsTransition(fromStep, toStep)
    const isLeadsExit = isLeadsExitFromStep(fromStep)

    if (stored) {
        const legacyTriggers = readLegacyTriggers(stored)

        if (isConversionEdge) {
            const conversionRules = normalizeConversionRules(
                stored.conversion_rules,
                legacyTriggers,
                stored.require_pm_approval ?? false,
            )
            return {
                exit_mode: 'conversion',
                triggers: enabledTriggersFromRules(conversionRules),
                conversion_rules: conversionRules,
                require_pm_approval: rulesHavePmApproval(conversionRules),
                review_prompt: stored.review_prompt,
            }
        }

        if (isLeadsExit) {
            return normalizeLeadsExitConfig({
                ...stored,
                triggers: legacyTriggers,
            })
        }

        return {
            exit_mode:
                stored.exit_mode ?? defaultExitModeForFromType(fromStep.type),
            triggers: normalizeTriggers(fromStep.type, legacyTriggers),
            require_pm_approval: stored.require_pm_approval ?? false,
            review_prompt: stored.review_prompt,
        }
    }

    const legacyProspectsConfig = fromStep.config as
        | { lead_conversion_triggers?: string[] }
        | undefined
    if (
        isConversionEdge &&
        legacyProspectsConfig?.lead_conversion_triggers?.length
    ) {
        const conversionRules = normalizeConversionRules(
            undefined,
            legacyProspectsConfig.lead_conversion_triggers,
            false,
        )
        return {
            exit_mode: 'conversion',
            triggers: enabledTriggersFromRules(conversionRules),
            conversion_rules: conversionRules,
            require_pm_approval: false,
        }
    }

    if (isConversionEdge) {
        const conversionRules = defaultConversionRules()
        return {
            exit_mode: 'conversion',
            triggers: enabledTriggersFromRules(conversionRules),
            conversion_rules: conversionRules,
            require_pm_approval: false,
        }
    }

    if (isLeadsExit) {
        const legacyFromCompletion = fromStep.completion?.condition
        if (legacyFromCompletion && legacyFromCompletion !== 'lead_created') {
            const legacyTriggers = normalizeTriggers(fromStep.type, [
                legacyFromCompletion,
                PM_MANUAL_TRIGGER,
            ])
            return normalizeLeadsExitConfig({ triggers: legacyTriggers })
        }
        return defaultLeadsExitTransitionConfig()
    }

    const legacyFromCompletion = fromStep.completion?.condition
    if (legacyFromCompletion) {
        const triggers = normalizeTriggers(fromStep.type, [
            legacyFromCompletion,
            PM_MANUAL_TRIGGER,
        ])
        const requirePmApproval =
            fromStep.on_complete?.action === 'require_review'
        return {
            exit_mode: 'any',
            triggers,
            require_pm_approval: requirePmApproval,
            review_prompt: fromStep.on_complete?.review_prompt,
        }
    }

    return defaultTransitionConfig(fromStep)
}

export function migrateDefinitionTransitionConfigs(
    definition: PipelineDefinition,
): PipelineDefinition {
    const steps = Array.isArray(definition.steps) ? definition.steps : []
    const enabled = getEnabledStepsInOrder(steps)

    const transitionConfigs = definition.transition_configs
        ? { ...definition.transition_configs }
        : {}

    for (let index = 0; index < enabled.length - 1; index += 1) {
        const fromStep = enabled[index]
        const toStep = enabled[index + 1]
        const key = transitionId(fromStep.id, toStep.id)
        if (!transitionConfigs[key]) {
            transitionConfigs[key] = getTransitionConfig(
                fromStep,
                toStep,
                transitionConfigs,
            )
        } else {
            transitionConfigs[key] = getTransitionConfig(fromStep, toStep, {
                [key]: transitionConfigs[key],
            })
        }
    }

    const cleanedSteps = steps.map((step) => {
        if (step.type !== 'prospects' || !step.config) {
            return step
        }
        const config = { ...step.config } as Record<string, unknown>
        if ('lead_conversion_triggers' in config) {
            delete config.lead_conversion_triggers
        }
        return { ...step, config }
    })

    return {
        ...definition,
        steps: cleanedSteps,
        transition_configs: transitionConfigs,
    }
}

/** @deprecated Use getTransitionConfig */
export function getProspectsToLeadsTransitionConfig(
    fromStep: PipelineStepInstance,
    toStep: PipelineStepInstance,
    transitionConfigs?: PipelineDefinition['transition_configs'],
) {
    return getTransitionConfig(fromStep, toStep, transitionConfigs)
}

/** @deprecated Use defaultTransitionConfig */
export function defaultProspectsToLeadsTransitionConfig() {
    return {
        exit_mode: 'conversion' as const,
        triggers: defaultTriggersForStep('prospects'),
        require_pm_approval: false,
    }
}
